import i18n from 'src/i18n'
import { useEffect, useRef, useState } from 'react'
import clsx from 'clsx'
import { extensions } from 'src/pages/CommunityDetails/Posts/tiptap/utils/extensionsTiptapEditor'
import { TiptapMethods, TiptapEditor } from 'src/pages/CommunityDetails/Posts/tiptap'
import { PostType } from 'src/hook/usePosts'

import { useMentionTrackEvents } from 'src/hook/useMentionTrackEvents'
import {
  OnErrorParams,
  OnSuccessParams
} from 'src/components/postCommentInput/hooks/useMentionUtils'
import { toastEventDispatch } from '@space-lib-events/toast'
import { ToastPosition, ToastType } from '@space-lib-events/toast/enum'
import { CommunityTypeConsume } from 'src/pages/communityHome'
import { ComponentName, TryResult } from 'src/utils/datahubDefaultEvent'
import { useCommunityDetails } from 'src/pages/CommunityDetails/Posts/context/communityDetailsContext'
import { MentionItem } from 'src/components/postCommentInput/components/mentionList'
import { PostModalLinkPreview } from '../postModalLinkPreview'
import {
  HandleOnChangePostModalEditorContent,
  PostModalContentProps
} from './postModalContent.types'
import { CustomInput, InputCounter } from './postModalContent.styles'
import { MAX_CHARACTERS } from './postModalContent.constants'

const PostModalContent = ({
  linkUrl,
  postType,
  mentions,
  setPostType,
  setTitle,
  setLinkUrl,
  description,
  isFileUploaded,
  setDescription,
  setDescriptionOnlyText,
  setMentions,
  title = '',
  isFullScreen = false,
  isOpenUpload = false
}: PostModalContentProps) => {
  const inputRefEditor = useRef<TiptapMethods>(null)
  const { sendChosenMentionEvent, sendMentionResultsEvent } = useMentionTrackEvents()
  const { community } = useCommunityDetails()
  const [isFirstUpdate, setIsFirstUpdate] = useState(true)

  useEffect(() => {
    if (isFirstUpdate && description !== '') {
      inputRefEditor.current?.editor?.commands.setContent(description as string, false, {
        preserveWhitespace: 'full'
      })
      setIsFirstUpdate(false)
    }
  }, [description])

  const handleTitle = (e: React.FormEvent<HTMLInputElement>) =>
    setTitle(e.currentTarget?.value as string)

  const classNameTipTap = clsx(
    '_text-gray-500 _text-2 _mb-0 _border-0 _w-full _overflow-y-scroll',
    {
      '_mb-4': isOpenUpload
    }
  )

  const handleOnChange = ({
    description,
    descriptionOnlyText,
    linkUrl,
    mentions
  }: HandleOnChangePostModalEditorContent) => {
    setDescription(description)
    setDescriptionOnlyText(descriptionOnlyText)
    setLinkUrl(linkUrl)
    setMentions?.(mentions || [])
  }

  const handleRemoveLinkPreview = () => {
    setLinkUrl('')

    if (postType === PostType.URL) setPostType(PostType.TEXT)
  }

  /* istanbul ignore next */
  const handleMentionSuccess = ({ query, length }: OnSuccessParams) => {
    sendMentionResultsEvent({
      communityId: Number(community.id),
      communityType: CommunityTypeConsume[community.type],
      communityUserRole: community.permissions?.group,
      componentName: ComponentName.POST_CREATE,
      componentSource: ComponentName.POST_CREATE,
      hasAccessToTheCommunity: true,
      parentEntityId: String(community.id),
      result: TryResult.SUCCESSFUL,
      resultCount: length,
      searchedString: query
    })
  }

  /* istanbul ignore next */
  const handleMentionError = ({ query, error }: OnErrorParams) => {
    const failureDescription =
      typeof error === 'object' ? JSON.stringify(error)?.slice(0, 255) : error

    sendMentionResultsEvent({
      communityId: Number(community.id),
      communityType: CommunityTypeConsume[community.type],
      communityUserRole: community.permissions?.group,
      componentName: ComponentName.POST_CREATE,
      componentSource: ComponentName.POST_CREATE,
      failureDescription,
      hasAccessToTheCommunity: true,
      parentEntityId: String(community.id),
      result: TryResult.FAILURE,
      searchedString: query,
      statusCode: error?.statusCode
    })

    toastEventDispatch({
      type: ToastType.DANGER,
      message: i18n.t('comments.mentions.request_error'),
      position: ToastPosition.BOTTOM_RIGHT
    })
  }

  /* istanbul ignore next */
  const handleMentionClick = ({ id, type, query, label, reference }: MentionItem) => {
    const mentionId = String(id).replace(/@.+?#/, '')

    sendChosenMentionEvent({
      communityId: Number(community.id),
      communityType: CommunityTypeConsume[community.type],
      communityUserRole: community.permissions?.group,
      componentName: ComponentName.POST_CREATE,
      hasAccessToTheCommunity: true,
      parentEntityId: String(community.id),
      searchedString: query,
      chosenStringHeadline: label,
      chosenStringSubtitle: reference,
      mentionId,
      mentionType: type
    })
  }

  return (
    <>
      <div className="_d-flex _position-relative _mb-3">
        <InputCounter
          data-test="input-counter"
          className="_h-full _text-1 _text-gray-500 _position-absolute _d-flex _flex-row _align-items-center _justify-content-start"
        >
          <span>{title?.length || 0}</span>
          <span>/</span>
          <span>{MAX_CHARACTERS}</span>
        </InputCounter>

        <CustomInput
          name="input-title"
          className="_pr-9 _text-5 _font-weight-bold _w-full _bg-transparent _border-0 _text-gray-900 _px-0"
          type="text"
          placeholder={i18n.t('modals.publish_post.input_title')}
          aria-label={i18n.t('modals.publish_post.input_title')}
          onChange={handleTitle}
          value={title}
          maxLength={MAX_CHARACTERS}
          autoComplete="off"
          autoFocus
        />
      </div>

      <TiptapEditor
        postType={postType}
        ref={inputRefEditor}
        content={description}
        extensions={extensions}
        className={classNameTipTap}
        isFullScreen={isFullScreen}
        isOpenUpload={isOpenUpload}
        isFileUploaded={isFileUploaded}
        mentions={mentions}
        placeholder={i18n.t('modals.publish_post.placeholder_description_textarea')}
        onRemoveLinkPreview={handleRemoveLinkPreview}
        onChange={handleOnChange}
        onMentionSuccess={handleMentionSuccess}
        onMentionError={handleMentionError}
        onMentionClick={handleMentionClick}
      />

      {linkUrl && (
        <PostModalLinkPreview
          className="_mb-4"
          linkUrl={linkUrl}
          onRemove={handleRemoveLinkPreview}
        />
      )}
    </>
  )
}

export default PostModalContent
